import React from "react";
import moment from "moment";
import { Box } from "@mui/material";
import { FiXCircle } from "react-icons/fi";
import {
  HiOutlineCalendarDays,
  HiOutlineRectangleGroup,
  HiOutlineShieldCheck,
} from "react-icons/hi2";
import { formatAmountWithCurrency } from "../../../../../../utils/CurrencyUtils";
import {
  Table,
  Empty,
  byDate,
  payoutLabel,
  paymentName,
  StatusPill,
  CountPill,
  formatStatus,
  rowStyle,
} from "../primitives";

const tdStyle = { padding: "10px 16px" };
const tdDarkStyle = { padding: "10px 16px", color: "#0d0d0e" };

// Local port of the portal's Changes/components/StatCard.
const StatCard = ({ icon: Icon, label, value, valueColor = "#111827" }) => {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        padding: "16px",
        backgroundColor: "#fff",
        border: "1px solid #E5E7EB",
        borderRadius: "12px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          color: "#6B7280",
          fontSize: "14px",
        }}
      >
        {Icon && <Icon style={{ width: "16px", height: "16px" }} />}
        <span>{label}</span>
      </div>
      <div style={{ fontSize: "24px", fontWeight: 600, color: valueColor }}>
        {value}
      </div>
    </div>
  );
};

// Where the window LANDS after this save, not where it is now — the two
// contradicted each other while an edit was in progress. Year printed once
// when both ends fall in it, so the value fits the card at its normal size.
const windowText = (window) => {
  const from = window?.start_date ? moment(window.start_date) : null;
  const to = window?.end_date ? moment(window.end_date) : null;

  if (!from && !to) return "Not set";
  if (!from) return to.format("DD MMM, YYYY");
  if (!to) return from.format("DD MMM, YYYY");

  return from.isSame(to, "year")
    ? `${from.format("DD MMM")} → ${to.format("DD MMM, YYYY")}`
    : `${from.format("DD MMM, YYYY")} → ${to.format("DD MMM, YYYY")}`;
};

/**
 * A date as it will be saved, with the value it replaces struck through beside
 * it. Same treatment as the pickers on the earlier steps, so a row that moved
 * reads the same way here as it did where it was edited.
 */
const DateValue = ({ value, original }) => {
  const changed = (original || null) !== (value || null);

  return (
    <span
      style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}
    >
      <span
        style={
          changed
            ? { color: "#0d0d0e", fontWeight: 500 }
            : { color: "#4B5563" }
        }
      >
        {value ? moment(value).format("DD MMM, YYYY") : "-"}
      </span>
      {changed && original && (
        <span
          style={{
            fontSize: "12px",
            color: "#9CA3AF",
            textDecoration: "line-through",
          }}
        >
          {moment(original).format("DD MMM")}
        </span>
      )}
    </span>
  );
};

// Flat: no card border, radius or inset padding — the section reads as a
// heading followed by its table, the same as every other step.
const Section = ({ title, subtitle, right, children }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "12px",
        flexWrap: "wrap",
      }}
    >
      <div>
        <h3
          style={{
            fontSize: "18px",
            fontWeight: 500,
            color: "#0d0d0e",
            margin: 0,
          }}
        >
          {title}
        </h3>
        {subtitle && (
          <p
            style={{
              fontSize: "12px",
              color: "#6B7280",
              marginTop: "2px",
              marginBottom: 0,
            }}
          >
            {subtitle}
          </p>
        )}
      </div>
      {right}
    </div>
    {children}
  </div>
);

// Shared by the two tables in the money section so their columns line up.
const MONEY_COLUMN_WIDTHS = ["45%", "25%", "30%"];

const SubHeading = ({ children, count }) => (
  <div
    style={{
      display: "flex",
      alignItems: "baseline",
      gap: "8px",
      marginTop: "8px",
    }}
  >
    <h4 style={{ fontSize: "14px", color: "#949daa", fontWeight: 400, margin: 0 }}>
      {children}
    </h4>
    {count != null && (
      <span style={{ fontSize: "12px", color: "#9CA3AF" }}>· {count}</span>
    )}
  </div>
);

/**
 * Final step — the schedule as it will stand once saved, read top to bottom:
 * the window it lands in, every activity in it, then the money that follows
 * from those dates.
 *
 * Unchanged rows are listed too: this reads as the finished schedule rather
 * than as a diff, so anything left out would look like a gap. Every value comes
 * from the backend's preview; the UI never computes a date.
 */
const ReviewStep = ({ schedule, preview, previewing }) => {
  const plan = preview?.plan || {};
  const blockers = preview?.blockers || [];

  // Projected values, keyed for overlay onto the stored rows. String keys
  // throughout: ids come back as numbers on some payloads, strings on others.
  const activityPlan = new Map(
    (plan.activities || []).map((a) => [String(a.id), a.to])
  );
  const groupPlan = new Map(
    (plan.groups || []).map((g) => [String(g.group_id), g.to])
  );
  const payoutPlan = new Map(
    (plan.payouts || []).map((p) => [String(p.id), p.to])
  );
  const paymentPlan = new Map(
    (plan.payment_lines || []).map((l) => [String(l.id), l.to])
  );

  const scopeWindow = plan.bid_project?.to ?? schedule.bid_project;
  const projectWindow = plan.project?.to ?? schedule.project;

  // ---- section 2: every activity, at the dates this save will set ----------
  const activities = [...(schedule.activities || [])]
    .map((activity) => {
      const projected = activityPlan.get(String(activity.id));
      return {
        ...activity,
        shownStart: projected ? projected.start_date : activity.start_date,
        shownEnd: projected ? projected.end_date : activity.end_date,
      };
    })
    .sort(byDate((activity) => activity.shownStart));

  // ---- section 3: the money ------------------------------------------------
  const groupById = new Map(
    (schedule.billing_milestones || []).map((g) => [String(g.group_id), g])
  );

  // Billing milestones are not shown any more, but a payout still takes its
  // date from one — those two have drifted apart in the data, and the
  // milestone is the one that is right.
  const groupDate = (group) => {
    const projected = groupPlan.get(String(group.group_id));
    return projected !== undefined ? projected : (group.due_date ?? null);
  };

  const payouts = [...(schedule.contractor_payouts || [])]
    .map((payout) => {
      const group = groupById.get(String(payout.group_id));
      const projected = payoutPlan.get(String(payout.id));
      return {
        ...payout,
        label: payoutLabel(payout, group?.name),
        shownDate: group
          ? groupDate(group)
          : projected !== undefined
            ? projected
            : payout.due_date,
      };
    })
    .sort(byDate((payout) => payout.shownDate));

  const payments = [...(schedule.customer_payments || [])]
    .map((line) => {
      const projected = paymentPlan.get(String(line.id));
      return {
        ...line,
        shownDate: projected !== undefined ? projected : line.due_date,
      };
    })
    .sort(byDate((line) => line.shownDate));

  const warrantyGroup = (schedule.billing_milestones || []).find(
    (g) => String(g.type || "").toUpperCase() === "PROJECT_WARRANTY_COMPLETION"
  );
  const warrantyDate = warrantyGroup ? groupDate(warrantyGroup) : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      {/* Not a warning — this is what stops the save, so it stays. */}
      {blockers.length > 0 && (
        <div
          style={{
            border: "1px solid #FEE2E2",
            backgroundColor: "#FEF2F2",
            borderRadius: "12px",
            padding: "16px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              color: "#B91C1C",
              fontSize: "12px",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              marginBottom: "8px",
            }}
          >
            <FiXCircle size={14} />
            Fix before saving · {blockers.length}
          </div>
          <ul
            style={{
              listStyle: "none",
              margin: 0,
              padding: 0,
              display: "flex",
              flexDirection: "column",
              gap: "6px",
            }}
          >
            {blockers.map((blocker, index) => (
              <li
                key={`${blocker.code}-${index}`}
                style={{ fontSize: "14px", color: "#991B1B" }}
              >
                {blocker.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ---------------- 1. project timeline ---------------- */}
      <Section
        title="Project Timeline"
        right={
          previewing ? (
            <span style={{ fontSize: "12px", color: "#9CA3AF" }}>
              Recalculating…
            </span>
          ) : schedule.project?.status ? (
            <StatusPill status={schedule.project.status} />
          ) : null
        }
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              lg: "repeat(3, 1fr)",
            },
            gap: "16px",
          }}
        >
          <StatCard
            icon={HiOutlineRectangleGroup}
            label="This scope"
            value={windowText(scopeWindow)}
          />
          <StatCard
            icon={HiOutlineCalendarDays}
            label="Whole project"
            value={windowText(projectWindow)}
          />
          <StatCard
            icon={HiOutlineShieldCheck}
            label="Warranty ends"
            value={
              warrantyDate
                ? moment(warrantyDate).format("DD MMM, YYYY")
                : "Not set"
            }
            valueColor={warrantyDate ? "#111827" : "#9CA3AF"}
          />
        </Box>
      </Section>

      {/* ---------------- 2. schedule ---------------- */}
      <Section
        title="Schedule"
        right={
          <CountPill>{activities.length} activities</CountPill>
        }
      >
        {!activities.length ? (
          <Empty>This scope has no activities yet.</Empty>
        ) : (
          <Table columns={["Activity", "Status", "Start", "End"]}>
            {activities.map((activity) => (
              <tr key={activity.id} style={rowStyle}>
                <td style={tdDarkStyle}>{activity.name}</td>
                <td style={tdStyle}>
                  <StatusPill status={activity.status} />
                </td>
                <td style={tdStyle}>
                  <DateValue
                    value={activity.shownStart}
                    original={activity.start_date}
                  />
                </td>
                <td style={tdStyle}>
                  <DateValue
                    value={activity.shownEnd}
                    original={activity.end_date}
                  />
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Section>

      {/* ---------------- 3. payments & payouts ---------------- */}
      <Section
        title="Payments & Payouts"
      >
        <SubHeading count={payments.length}>Customer payments</SubHeading>
        {!payments.length ? (
          <Empty>No customer payments on this scope.</Empty>
        ) : (
          <Table
            columns={["Payment", "Amount", "Due date"]}
            widths={MONEY_COLUMN_WIDTHS}
          >
            {payments.map((line) => (
              <tr key={line.id} style={rowStyle}>
                <td style={tdDarkStyle}>
                  {paymentName(line, formatStatus)}
                </td>
                <td style={tdDarkStyle}>
                  {line.amount ? formatAmountWithCurrency(line.amount, "black-xs-small") : "-"}
                </td>
                <td style={tdStyle}>
                  <DateValue value={line.shownDate} original={line.due_date} />
                </td>
              </tr>
            ))}
          </Table>
        )}

        <SubHeading count={payouts.length}>Contractor payouts</SubHeading>
        {!payouts.length ? (
          <Empty>No contractor payouts on this scope yet.</Empty>
        ) : (
          <Table
            columns={["Payout", "Amount", "Due date"]}
            widths={MONEY_COLUMN_WIDTHS}
          >
            {payouts.map((payout) => (
              <tr key={payout.id} style={rowStyle}>
                <td style={tdDarkStyle}>{payout.label}</td>
                <td style={tdDarkStyle}>
                  {payout.amount ? formatAmountWithCurrency(payout.amount, "black-xs-small") : "-"}
                </td>
                <td style={tdStyle}>
                  <DateValue
                    value={payout.shownDate}
                    original={payout.due_date}
                  />
                </td>
              </tr>
            ))}
          </Table>
        )}

      </Section>
    </div>
  );
};

export default ReviewStep;
